// Opponent's shot replay: the shooter streams sampled ball positions (shot.ts) and we play them back with a small
// buffer instead of simulating, then snap to the server's state once the replay has caught up.
import Balls from "../Balls";
import SoundManager from "../SoundManager";
import type {Vec} from "../shared/rules";
import type {MatchCore, Playback, StreamFrame} from "./types";
import type {FramesMsg, ShotStartMsg} from "./messages";

export const PLAYBACK_DELAY_MS = 300;  // replay this far behind the stream, to ride out network jitter
export const STALL_STRETCH_MS = 400;   // past the last frame by more than this: stretch time instead of freezing
export const FINISH_AFTER_MS = 2500;   // result in, replay not caught up yet: apply it anyway after this
export const FINISH_EMPTY_MS = 1500;   // result in, no frame ever arrived: apply it after this
export const QUIET_RESYNC_MS = 6000;   // nothing arriving for this long: ask the server (resync)...
export const RESYNC_EVERY_MS = 5000;   // ...at most this often

export function newPlayback(now: number, shotId: string | null): Playback {
    return {t0: now, frames: [], pendingState: null, stateAt: 0, lastIndex: -1, shotId, lastData: now, lastSync: now};
}

/** Streamed frames that look like frames (t and p); the rest are dropped. */
export function acceptFrames(pb: Playback, frames: StreamFrame[]) {
    for (const f of frames) if (f && typeof f.t === "number" && f.p) pb.frames.push(f);
}

export interface PlaybackStep {
    /** frames passed since the last step: a ball-hit sound for each one with contacts */
    hits: number;
    /** the two frames to interpolate between (null: nothing to show yet) */
    a: StreamFrame | null;
    b: StreamFrame | null;
    alpha: number;
    /** apply the pending server state now */
    finish: boolean;
    /** ask the server where things stand */
    resync: boolean;
}

/** Advances the replay to `now` (updates t0 on a stall, lastIndex and lastSync). No engine access. */
export function stepPlayback(pb: Playback, now: number): PlaybackStep {
    const frames = pb.frames;
    const step: PlaybackStep = {hits: 0, a: null, b: null, alpha: 1, finish: false, resync: false};
    if (frames.length) {
        let t = now - pb.t0 - PLAYBACK_DELAY_MS;
        const last = frames[frames.length - 1];
        // network stall: stretch time rather than freezing at the last frame forever
        if (t > last.t + STALL_STRETCH_MS && !pb.pendingState) { pb.t0 += t - last.t - STALL_STRETCH_MS; t = last.t + STALL_STRETCH_MS; }
        let i = pb.lastIndex;
        while (i + 1 < frames.length && frames[i + 1].t <= t) i++;
        for (let k = pb.lastIndex + 1; k <= i; k++) if (frames[k].h) step.hits++;
        pb.lastIndex = i;
        const a = frames[Math.max(i, 0)], b = frames[Math.min(i + 1, frames.length - 1)];
        const span = b.t - a.t;
        step.a = a;
        step.b = b;
        step.alpha = span > 0 ? Math.min(1, Math.max(0, (t - a.t) / span)) : 1;
        if (pb.pendingState && (i >= frames.length - 1 || now - pb.stateAt > FINISH_AFTER_MS)) step.finish = true;
    } else if (pb.pendingState && now - pb.stateAt > FINISH_EMPTY_MS) {
        step.finish = true;
    }
    // Nothing arriving for a while (shooter's connection dropped, lost messages): ask the server
    // where things stand. If the shot is still pending we keep waiting, otherwise we snap to its state.
    if (!pb.pendingState && now - pb.lastData > QUIET_RESYNC_MS && now - pb.lastSync > RESYNC_EVERY_MS) {
        pb.lastSync = now;
        step.resync = true;
    }
    return step;
}

/** Where a ball is between two frames; null = it is not on the table (potted). */
export function interpolateBall(a: StreamFrame, b: StreamFrame, id: string, alpha: number): Vec | null {
    const pa = a.p[id];
    if (!pa) return null;
    const pbp = b.p[id] || pa;
    return [pa[0] + (pbp[0] - pa[0]) * alpha, pa[1] + (pbp[1] - pa[1]) * alpha];
}

// ----- called through Match -----

/** Per rendered frame while spectating. */
export function playFrames(m: MatchCore) {
    const pb = m.playback!;
    const step = stepPlayback(pb, performance.now());
    for (let k = 0; k < step.hits; k++) SoundManager.PlayBallHit();
    if (step.a && step.b) {
        for (const ball of Balls.list) {
            const p = interpolateBall(step.a, step.b, ball.id, step.alpha);
            if (!p) { if (!ball.Killed) Balls.kill(ball); continue; }
            Balls.show(ball, p, true);   // rolling: the mesh turns with the move
        }
    }
    if (step.finish) m.finishPlayback();
    if (step.resync) m.resync();
}

export function finishPlayback(m: MatchCore) {
    const st = m.playback?.pendingState;
    m.playback = null;
    if (st) m.applyServerState(st);
}

/** socket "game:shot-start": the opponent struck the cue ball. */
export function onShotStart(m: MatchCore, msg: ShotStartMsg) {
    if (!m.online || msg.gameId !== m.online.gameId || m.myTurn()) return;
    if (typeof msg.seq === "number" && msg.seq !== m.online.seq) return;   // stale shot
    if (m.phase === "spectating" && m.playback && m.playback.shotId === (msg.shot ?? null)) return;
    m.phase = "spectating";
    m.playback = newPlayback(performance.now(), msg.shot ?? null);
    if (msg.cueStart) Balls.show(Balls.byId.cue, msg.cueStart);
    m.publish();
}

/** socket "game:frames": positions of the opponent's shot. */
export function onFrames(m: MatchCore, msg: FramesMsg) {
    if (!m.online || msg.gameId !== m.online.gameId || m.myTurn()) return;
    // frames of an earlier or cancelled shot are ignored (the server drops them too)
    if (typeof msg.seq === "number" && msg.seq !== m.online.seq) return;
    if (m.phase === "spectating" && m.playback && m.playback.shotId && msg.shot && m.playback.shotId !== msg.shot) return;
    if (m.phase !== "spectating") m.onShotStart({gameId: msg.gameId, seq: msg.seq, shot: msg.shot});
    const pb = m.playback;
    if (!pb || !Array.isArray(msg.frames)) return;
    pb.lastData = performance.now();
    if (!pb.shotId && msg.shot) pb.shotId = msg.shot;
    acceptFrames(pb, msg.frames);
}
